import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, Filter, MessageSquare, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';
import { SMS_TEXT_MAX_LENGTH } from '@/features/campaign-texts/smsTextLimit';
import { MAX_SELECTED_TEXTS } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { TagFilterDropdown } from '@/features/smswiser-campaigns/components/shared';
import { CreativeAddRow, CreativeSearchInput, TextPickerItem } from '../CreativePickerPrimitives';
import type { TextsPickerSectionProps } from '../drop-modals.types';
import { useTextsFilter } from './texts';

export const TextsPickerSection: FC<TextsPickerSectionProps> = (props) => {
  const {
    textsList, selectedTexts, onToggleText, onSelectAll, onClearSelection,
    onDeleteText, onDeleteTextsBulk, search, onSearch, newValue, setNewValue,
    onAdd, isBusy, expanded, onToggleExpand, tagsByTextId, tagColors,
    smsTextsWithIds, pendingHideTexts, replacedByAiTexts, actionSlot, toolbarSlot,
  } = props;

  const textToTemplateId = useMemo(() => {
    if (!smsTextsWithIds?.length) return undefined;
    return Object.fromEntries(smsTextsWithIds.map((t) => [t.text, t.templateId]));
  }, [smsTextsWithIds]);

  const { success: toastSuccess } = useToast();
  const [showAddRow, setShowAddRow] = useState(false);
  const [textTagFilter, setTextTagFilter] = useState('');

  const { availableTextTags, getTagsForText, filtered } = useTextsFilter({
    textsList, search, tagFilter: textTagFilter, tagsByTextId, textToTemplateId, selectedTexts,
  });

  const handleAdd = useCallback(async () => {
    await onAdd?.();
    if (textsList.length > 0) setShowAddRow(false);
  }, [onAdd, textsList.length]);

  const handleDeleteSelected = useCallback(async () => {
    const toDelete = [...selectedTexts];
    if (onDeleteTextsBulk) await onDeleteTextsBulk(toDelete);
    else if (onDeleteText) for (const t of toDelete) await onDeleteText(t);
    onClearSelection?.();
    toastSuccess('Deleted', `${toDelete.length} text${toDelete.length > 1 ? 's' : ''} removed`);
  }, [selectedTexts, onDeleteTextsBulk, onDeleteText, onClearSelection, toastSuccess]);

  const selectLabel = selectedTexts.length >= Math.min(filtered.length, MAX_SELECTED_TEXTS) && selectedTexts.length > 0
    ? 'Deselect all' : 'Select all';

  return (
    <section className="flex min-h-0 flex-col space-y-2 xl:min-w-0">
      <button
        type="button"
        onClick={onToggleExpand}
        className="flex w-full items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-muted/50"
      >
        <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <MessageSquare className="h-3.5 w-3.5" />
          Campaign texts ({textsList.length})
          <span className={cn(
            'rounded px-1.5 py-0.5 text-xs',
            selectedTexts.length > MAX_SELECTED_TEXTS ? 'bg-destructive/20 text-destructive font-semibold' : 'bg-primary/20 text-primary',
          )}>
            {selectedTexts.length}/{MAX_SELECTED_TEXTS} selected
          </span>
        </h4>
        <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
      </button>
      {expanded && (
        <div
          className={cn(
            'flex min-h-[280px] max-h-[min(600px,70vh)] flex-col gap-2 overflow-y-auto rounded-lg border border-border bg-muted/10 xl:min-h-[320px]',
            toolbarSlot ? 'p-0' : 'p-3',
          )}
        >
          {toolbarSlot && <div className="shrink-0">{toolbarSlot}</div>}
          <div className={cn('flex shrink-0 flex-wrap items-center justify-between gap-2', toolbarSlot && 'px-3 pt-2')}>
            <label className="text-xs font-medium text-muted-foreground">Filter</label>
            <div className="flex flex-wrap items-center gap-2">
              {availableTextTags.length > 0 && (
                <div className="flex shrink-0 items-center gap-2">
                  <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                  <TagFilterDropdown
                    value={textTagFilter}
                    options={availableTextTags}
                    tagColors={tagColors}
                    displayLabel={textTagFilter === '' ? 'All tags' : textTagFilter}
                    onSelect={setTextTagFilter}
                    placeholder="All tags"
                    compact
                  />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-1.5">
                <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={onSelectAll}>
                  {selectLabel}
                </Button>
                {onAdd && (
                  <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 text-xs" onClick={() => setShowAddRow((v) => !v)} aria-expanded={showAddRow}>
                    <ChevronDown className={cn('h-3 w-3 shrink-0 transition-transform', showAddRow && 'rotate-180')} /> Add text
                  </Button>
                )}
                {actionSlot}
                {selectedTexts.length > 0 && (onClearSelection || onDeleteText) && (
                  <button type="button" className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md p-0 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10" disabled={isBusy} onClick={handleDeleteSelected} aria-label={`Delete ${selectedTexts.length} selected`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className={toolbarSlot ? 'px-3' : ''}>
            <CreativeSearchInput value={search} onChange={onSearch} placeholder="Filter..." />
          </div>
          {onAdd && (showAddRow || textsList.length === 0) && (
            <div className={cn('shrink-0 rounded-md border border-border/60 bg-background/50 p-2', toolbarSlot && 'mx-3')}>
              <CreativeAddRow
                value={newValue}
                onChange={setNewValue}
                placeholder="New text or paste multiple (JSON / lines)"
                onAdd={handleAdd}
                disabled={!newValue.trim() || isBusy}
                inputDisabled={isBusy}
                multiline
                smsSegmentLengthLimit={SMS_TEXT_MAX_LENGTH}
                onKeyDown={(e) =>
                  e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleAdd())
                }
              />
            </div>
          )}
          <div className={cn('min-h-[120px] min-w-0 flex-1 space-y-1 overflow-y-auto', toolbarSlot && 'px-3 pb-3')}>
            {filtered.map((text) => (
              <TextPickerItem
                key={text}
                text={text}
                isSelected={selectedTexts.includes(text)}
                onToggle={() => onToggleText(text)}
                performanceTags={getTagsForText(text)}
                tagColors={tagColors}
                pendingHide={pendingHideTexts?.includes(text)}
                replacedByAi={replacedByAiTexts?.includes(text)}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
